import axios from "axios";
import { EventEmitter } from "events";
import { createContext, useContext, useState } from "react";

const ClientContext = createContext();

const ClientEvents = new EventEmitter();

export const ClientProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  const [searchParams, setSearchParams] = useState({
    search: "",
  });

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const createClient = async (data) => {
    try {
      const response = await axios.post(`/clients`, data);
      const client = response.data;
      setClients((prev) => [client, ...prev]);
      ClientEvents.emit("client:created");
      return client;
    } catch (error) {
      console.error("Error creating client:", error);
      throw error;
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {
        ...searchParams,
        page: pagination.page,
        limit: pagination.limit,
      };
      const response = await axios.get(`/clients`, { params });
      const { data, total, totalPages } = response.data;
      setClients(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      console.error("Error fetching clients:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchClientById = async (clientId) => {
    try {
      const response = await axios.get(`/clients/${clientId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching client by ID:", error);
      throw error;
    }
  };

  const updateClient = async (clientId, updatedData) => {
    try {
      const response = await axios.put(`/clients/${clientId}`, updatedData);
      const updatedClient = response.data;
      setClients((prev) =>
        prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
      );
      ClientEvents.emit("client:updated");
      return updatedClient;
    } catch (error) {
      console.error("Error updating client:", error);
      throw error;
    }
  };

  const deleteClient = async (clientId) => {
    try {
      await axios.delete(`/clients/${clientId}`);
      setClients((prev) => prev.filter((c) => c.id !== clientId));
      setPagination((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));
    } catch (error) {
      console.error("Error deleting client:", error);
      throw error;
    }
  };

  const activateClient = async (clientId) => {
    try {
      const response = await axios.put(`/clients/${clientId}/activate`);
      const updated = response.data;
      setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      ClientEvents.emit("client:updated");
      return updated;
    } catch (error) {
      console.error("Error activating client:", error);
      throw error;
    }
  };

  const suspendClient = async (clientId) => {
    try {
      const response = await axios.put(`/clients/${clientId}/suspend`);
      const updated = response.data;
      setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      ClientEvents.emit("client:updated");
      return updated;
    } catch (error) {
      console.error("Error suspending client:", error);
      throw error;
    }
  };

  const deactivateClient = async (clientId) => {
    try {
      const response = await axios.put(`/clients/${clientId}/deactivate`);
      const updated = response.data;
      setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      ClientEvents.emit("client:updated");
      return updated;
    } catch (error) {
      console.error("Error deactivating client:", error);
      throw error;
    }
  };

  const disconnectClient = async (clientId) => {
    try {
      await axios.post(`/clients/${clientId}/disconnect`);
    } catch (error) {
      console.error("Error disconnecting client:", error);
      throw error;
    }
  };

  const resetClientPassword = async (clientId) => {
    try {
      const response = await axios.put(`/clients/${clientId}/password/reset`);
      return response.data;
    } catch (error) {
      console.error("Error resetting client password:", error);
      throw error;
    }
  };

  const value = {
    clients,
    loading,
    pagination,
    searchParams,
    createClient,
    fetchClients,
    fetchClientById,
    updateClient,
    deleteClient,
    disconnectClient,
    activateClient,
    suspendClient,
    deactivateClient,
    resetClientPassword,
    updatePagination,
    updateSearchParams,
    ClientEvents,
  };

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
};

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClientContext must be used within a ClientProvider");
  }
  return context;
};
